import { type Enums, type ParsedUserProfile } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useRef, useState } from 'react'
import { View } from 'react-native'
import { useAlert } from '~/components/GlobalAlert.tsx'
import { Body, Label } from '~/components/typography/Content'
import { Heading1, Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input, type InputVariant } from '~/components/ui/input'
import { OTPInput } from '~/components/ui/input-otp'
import { Text } from '~/components/ui/text'
import { useSession } from '~/components/user/SessionProvider'
import { getCurrentUserProfile } from '~/data/user/readUserProfile'
import { verifyOrCreateUserProfile } from '~/data/user/writeUserProfile'
import { usePostHog } from '~/lib/analytics/posthog'
import { useChangeAuthContact, useSendOtp, useVerifyOtp } from '~/lib/auth/useOtpAuth'
import { queryClient, trpcClient } from '~/lib/trpc'
import { cn } from '~/lib/utils'

export default function Auth({
  allowAccountCreation = true,
  inputVariant = 'line',
  className,
  content,
  otpContent,
  userRole,
  onAuthenticated,
}: {
  allowAccountCreation?: boolean
  inputVariant?: InputVariant
  className?: string
  content?: React.ReactNode
  otpContent?: React.ReactNode
  userRole?: Enums<'user_role'>
  onAuthenticated: () => void
}) {
  const { t } = useTranslations('common')
  const [contact, setContact] = useState('')
  const [otp, setOtp] = useState('')
  const [showOtpForm, setShowOtpForm] = useState(false)
  const [loading, setLoading] = useState(false)

  const { showAlert } = useAlert()

  const [contactMethod, setContactMethod] = useState<'phone' | 'email'>('email')
  const didSetManualContactMethod = useRef(false)

  const posthog = usePostHog()
  const { isAnonymous } = useSession()

  const sendOtpMutation = useSendOtp({ allowAccountCreation })
  const verifyOtpMutation = useVerifyOtp()
  const startContactChange = useChangeAuthContact()
  const verifyChange = useVerifyOtp()

  const sendOtp = useCallback(async () => {
    const trimmed = contact.trim()

    if (!trimmed) {
      showAlert({
        type: 'error',
        title: t(($) => $.login.error.title),
        description: t(($) => $.login.error.emptyContact),
      })
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      if (isAnonymous) {
        if (contactMethod === 'email') {
          await startContactChange.mutateAsync({ email: trimmed })
        } else {
          await startContactChange.mutateAsync({ phone: trimmed })
        }
      } else {
        if (contactMethod === 'email') {
          await sendOtpMutation.mutateAsync({ email: trimmed })
        } else {
          await sendOtpMutation.mutateAsync({ phone: trimmed })
        }
      }
      setShowOtpForm(true)
    } catch {
      showAlert({
        type: 'error',
        title: t(($) => $.login.error.title),
        description: t(($) => $.login.error.sendFailed),
      })
    }

    setLoading(false)
  }, [contact, showAlert, isAnonymous, contactMethod, startContactChange, sendOtpMutation, t])

  const verifyOtp = useCallback(
    async (otpValue = otp) => {
      if (otpValue.length !== 6) {
        showAlert({
          type: 'error',
          title: t(($) => $.login.error.title),
          description: t(($) => $.login.error.invalidCode),
        })

        return
      }

      setLoading(true)
      const contactValue = contact.trim()

      try {
        const user = isAnonymous
          ? await (async () => {
              if (contactMethod === 'email') {
                const updated = await verifyChange.mutateAsync({
                  email: contactValue,
                  token: otpValue,
                  type: 'email_change',
                })

                return updated
              }

              const updated = await verifyChange.mutateAsync({
                phone: contactValue,
                token: otpValue,
                type: 'phone_change',
              })

              return updated
            })()
          : await verifyOtpMutation.mutateAsync(
              contactMethod === 'email'
                ? { email: contactValue, token: otpValue, type: 'email' }
                : { phone: contactValue, token: otpValue, type: 'sms' }
            )

        if (!user) {
          // eslint-disable-next-line i18next/no-literal-string
          throw new Error('No user after verification')
        }

        let userProfile: ParsedUserProfile | null = null

        if (userRole) {
          userProfile = await verifyOrCreateUserProfile({
            user,
            createRole: userRole,
          })

          if (!userProfile) {
            // eslint-disable-next-line i18next/no-literal-string
            throw new Error('No user profile created. This should not happen.')
          }
        } else {
          userProfile = await getCurrentUserProfile()
        }

        posthog.identify(user.id, {
          email: user.email ?? null,
          name: userProfile?.name ?? '(Anonymous)',
          phoneNumber: user.phone ?? null,
          role: userProfile?.roles?.[0] ?? null,
        })

        await queryClient.invalidateQueries({
          queryKey: ['userProfile'],
        })

        const hasRealtorRole =
          userProfile?.roles?.includes('realtor') ?? (userRole ? userRole === 'realtor' : false)

        if (hasRealtorRole) {
          void trpcClient.users.login.mutate().catch((loginError) => {
            posthog.captureException(loginError, {
              error_name: 'Login ping failed',
              userId: user.id,
              email: user.email ?? null,
              phone: user.phone ?? null,
              name: userProfile?.name ?? null,
              role: userProfile?.roles?.[0] ?? null,
            })
          })
        }

        onAuthenticated()
      } catch (error) {
        posthog.captureException(error, {
          error_name: 'Login Failed',
          loginMethod: contactMethod,
          contactValue,
        })

        showAlert({
          type: 'error',
          title: t(($) => $.login.error.title),
          description: t(($) => $.login.error.loginFailed),
        })
      }

      setLoading(false)
    },
    [
      otp,
      showAlert,
      contact,
      isAnonymous,
      verifyOtpMutation,
      contactMethod,
      userRole,
      onAuthenticated,
      verifyChange,
      posthog,
      t,
    ]
  )

  const onOtpValue = useCallback(
    (value: string) => {
      setOtp(value)

      if (value.length === 6) {
        setTimeout(() => verifyOtp(value), 100)
      }
    },
    [verifyOtp]
  )

  return (
    <View className={cn('w-full max-w-md gap-y-6', className)}>
      {!showOtpForm ? (
        <View className="gap-6">
          {content ?? <Heading2 className="text-center">{t(($) => $.login.title)}</Heading2>}
          <View className="w-full gap-2">
            {didSetManualContactMethod.current && (
              <View className="w-full flex-row flex-nowrap items-baseline justify-center text-nowrap">
                {contactMethod === 'phone' ? (
                  <>
                    <Label>{t(($) => $.login.phoneLabel)}</Label>
                    <Button
                      variant="link"
                      className="ml-3 inline-flex p-0"
                      onPress={() => {
                        setContactMethod('email')
                        didSetManualContactMethod.current = true
                      }}>
                      <ButtonLabel>{t(($) => $.login.useEmail)}</ButtonLabel>
                    </Button>
                  </>
                ) : (
                  <>
                    <Label>{t(($) => $.login.emailLabel)}</Label>
                    <Button
                      variant="link"
                      className="ml-3 inline-flex p-0"
                      onPress={() => {
                        setContactMethod('phone')
                        didSetManualContactMethod.current = true
                      }}>
                      <ButtonLabel>{t(($) => $.login.usePhone)}</ButtonLabel>
                    </Button>
                  </>
                )}
              </View>
            )}
            {contactMethod === 'phone' ? (
              <Input
                testID="auth-contact-input"
                ref={(node) => {
                  if (node) {
                    node.focus()
                  }
                }}
                type="phone"
                onChangeText={(display, _cc, stored) => setContact(stored || display)}
                placeholder={t(($) => $.login.phonePlaceholder)}
                editable={!loading}
                onSubmitEditing={sendOtp}
                value={contact}
                variant={inputVariant}
                autoFocus
              />
            ) : (
              <Input
                testID="auth-contact-input"
                ref={(node) => {
                  if (node) {
                    node.focus()
                  }
                }}
                autoCapitalize="none"
                autoComplete="email"
                editable={!loading}
                keyboardType="email-address"
                onChangeText={(value) => {
                  const trimmed = value.trim()
                  setContact(trimmed)

                  if (/^\d{3}/.test(trimmed)) {
                    setContactMethod('phone')
                  }
                }}
                onSubmitEditing={sendOtp}
                placeholder={t(($) => $.login.contactPlaceholder)}
                // eslint-disable-next-line i18next/no-literal-string
                returnKeyType="go"
                value={contact}
                variant={inputVariant}
              />
            )}
          </View>

          <Button
            testID="auth-send-otp-button"
            variant="primary"
            disabled={loading || !contact.trim()}
            onPress={sendOtp}
            loading={loading}>
            <Body className="font-medium text-primary-foreground">
              {t(($) => $.login.sendCode)}
            </Body>
          </Button>
        </View>
      ) : (
        <View className="gap-6">
          {otpContent ?? (
            <Heading1 className="text-center">{t(($) => $.login.almostReady)}</Heading1>
          )}
          <View className="gap-4">
            <Body className="text-pretty text-center text-lg/relaxed text-primary">
              {t(($) => $.login.enterCode)}
              <Text className="text-center font-semibold"> {contact}</Text>
            </Body>
            <View className="flex-row justify-center">
              <Button
                testID="auth-change-contact-button"
                variant="link"
                size="sm"
                disabled={loading}
                onPress={() => {
                  setShowOtpForm(false)
                  setOtp('')
                }}>
                {t(($) => $.login.changeContact)}
              </Button>
              <Button disabled={loading} onPress={sendOtp} variant="link">
                {t(($) => $.login.resendCode)}
              </Button>
            </View>
          </View>

          <View className="items-center gap-6">
            <OTPInput
              testID="auth-otp-input"
              maxLength={6}
              onChange={onOtpValue}
              onComplete={onOtpValue}
              value={otp}
              variant={inputVariant}
              inputClassName="w-40 flex-none text-center"
              textContentType="oneTimeCode"
            />

            <View className="w-full gap-3">
              <Button
                testID="auth-login-button"
                variant="primary"
                disabled={loading || otp.length < 6}
                onPress={() => verifyOtp(otp)}
                loading={loading}>
                {t(($) => $.login.loginButton)}
              </Button>
            </View>
          </View>
        </View>
      )}
    </View>
  )
}
